import { apiClient } from './client'

/** Read shape for `FiscalYear` — mirrors the backend contract
 * (`backend/.../module/config/api/dto/FiscalYearView.java`) exactly. */
export interface FiscalYearView {
  id: string
  entityType: string
  entityId: string
  /** The business-friendly reference (e.g. `"FY2025"`) — never a raw id. */
  label: string
  startDate: string
  endDate: string
  jurisdiction: string
  createdAt: string
  createdBy: string | null
}

/**
 * Thin wrapper (this UX-quality pass) over the already-published `GET /api/v1/fiscal-years/{id}`
 * (`FiscalYearController.get`, `module.config`, FR-M24.6) — no new backend route was needed. Several
 * frontend files (`useCaseContexts`, `CaseListView`/`ProgramFilingListView`'s own `fiscalYearLabel`
 * columns) carry a stale "no FiscalYear get-by-id read path yet" comment from when this endpoint
 * did not exist; it does now, so this client closes that disclosed gap for any screen that only
 * ever had a bare `fiscalYearId` to show.
 */
export async function getFiscalYear(id: string, signal?: AbortSignal): Promise<FiscalYearView> {
  const { data } = await apiClient.get<FiscalYearView>(`/fiscal-years/${id}`, { signal })
  return data
}

/** `POST /api/v1/fiscal-years` request shape (`CreateFiscalYearRequest.java`). `entityType` is
 * `legal_entity` or `tenant` — `entityId` is required unless `entityType === 'tenant'`. */
export interface CreateFiscalYearRequest {
  entityType: string
  entityId?: string | null
  label: string
  startDate: string
  endDate: string
  jurisdiction?: string | null
}

/**
 * IB-327 — `GET /api/v1/fiscal-years?entityType=...&entityId=...` (`FiscalYearController.list`).
 * Backs the "New Production" wizard's ProductionFY-shell step (a Production's fiscal years are
 * its own Legal entity's fiscal years).
 */
export async function listFiscalYears(
  entityType: string,
  entityId: string | null,
  signal?: AbortSignal,
): Promise<FiscalYearView[]> {
  const q = new URLSearchParams({ entityType })
  if (entityId) q.set('entityId', entityId)
  const { data } = await apiClient.get<FiscalYearView[]>(`/fiscal-years?${q.toString()}`, { signal })
  return data
}

/** `POST /api/v1/fiscal-years` (`FiscalYearController.create`) — used when the "New Production"
 * wizard's picker finds no existing fiscal year for the chosen Legal entity. */
export async function createFiscalYear(request: CreateFiscalYearRequest): Promise<FiscalYearView> {
  const { data } = await apiClient.post<FiscalYearView>('/fiscal-years', request)
  return data
}
